import { useMemo, useState } from "react";
import {
  Box,
  Chip,
  Divider,
  Stack,
  Tooltip,
  Typography,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import type { Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import InfoOutlined from "@mui/icons-material/InfoOutlined";

import type { BrokerAccount, Bot } from "@/data/mock-data";
import {
  aggregateBalances,
  deriveBalance
} from "@/data/mock-balances";
import type {
  BalanceMode,
  BalanceSnapshot,
  BalanceValues
} from "@/data/mock-balances";
import { formatUSD } from "@/lib/formatters";
import { MarginBufferBar } from "@/components/MarginBufferBar";
import { TERMS } from "@/lib/terms";
import type { TermKey } from "@/lib/terms";

// ---- Props ---------------------------------------------------------------

interface BalancesViewProps {
  /**
   * Accounts to include in the view. When only one is passed, the view
   * renders in single-account mode. When multiple are passed, balances
   * are aggregated and a subtle "X accounts" badge appears.
   */
  accounts: BrokerAccount[];
  /** All bots across the listed accounts — used for the exBots mode. */
  bots: Bot[];
  /**
   * Whether the caller is in aggregate "All Live Accounts" mode. Only
   * affects the header copy — aggregation itself is driven by the
   * accounts array having length > 1.
   */
  isAggregate?: boolean;
  /**
   * Compact variant — drops the hero section so only the sectioned
   * tables render. Used inside the hero's expandable drawer where the
   * big headline already sits above.
   */
  compact?: boolean;
}

// ---- Public component ----------------------------------------------------

/**
 * Robinhood-style balances page.
 *
 * Layout:
 *   1. Hero — Buying Power headline + Cash/Margin split + Total.
 *   2. MarginBufferBar — colored usage indicator.
 *   3. Section groups (Cash, Margin, Positions, High Yield Cash, Misc) —
 *      each a simple two-column "Last close / Current" table, matching
 *      the Alpaca reference. Dense but readable.
 *
 * A "Total / Excl. Bots" toggle in the hero flips every figure between
 * the two derivation modes. In exBots mode the numbers drop by the
 * amount currently allocated to running bots — gives users a clean
 * view of what they personally control vs. what the platform controls.
 */
export function BalancesView({
  accounts,
  bots,
  isAggregate = false,
  compact = false
}: BalancesViewProps) {
  const [mode, setMode] = useState<BalanceMode>("total");

  const snapshot: BalanceSnapshot = useMemo(() => {
    const perAccount = accounts.map((a) => deriveBalance(a, bots, mode));
    return aggregateBalances(perAccount);
  }, [accounts, bots, mode]);

  // A second snapshot pinned to exBots mode — used by the
  // PlainEnglishSummary callouts on the Total tab. The callouts only
  // surface in `total` view (where the dense figures show *all* the
  // account's money including bot allocations); the user wants the
  // callouts to translate that headline into "but here's what you
  // can actually use without disturbing bots". In exBots mode the
  // whole page is already bot-safe, so the callouts would be a tautology
  // and we hide them entirely. (Sergey 2026-04-30.)
  const exBotsSnapshot: BalanceSnapshot = useMemo(() => {
    if (mode === "exBots") return snapshot;
    const perAccount = accounts.map((a) => deriveBalance(a, bots, "exBots"));
    return aggregateBalances(perAccount);
  }, [accounts, bots, mode, snapshot]);

  // `lastClose` is intentionally unused — we removed the "Last close"
  // column 2026-04-27. The field stays on BalanceSnapshot for now to
  // avoid touching the public type's surface.
  const { current, margin } = snapshot;

  return (
    <Box>
      {!compact && (
        <HeroSection
          current={current}
          mode={mode}
          onModeChange={setMode}
          isAggregate={isAggregate}
          accountCount={accounts.length}
        />
      )}

      {compact && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          <ModeToggle value={mode} onChange={setMode} />
          {isAggregate && (
            <Chip
              size="small"
              label={`${accounts.length} accounts`}
              sx={(t) => ({
                bgcolor: alpha(t.palette.primary.main, 0.12),
                color: "primary.main",
                fontWeight: 600,
                height: 22,
                "& .MuiChip-label": { px: 1.25, fontSize: 11 }
              })}
            />
          )}
        </Stack>
      )}

      <Box sx={{ mt: compact ? 0 : 2 }}>
        <MarginBufferBar buffer={margin} />
      </Box>

      {/* Plain-English summary — sits between the margin buffer
       *  visualization and the dense Alpaca-style figure tables. The
       *  motivation (Sergey 2026-04-29):
       *
       *      "for regular foxs it should be clear how much I can use for
       *       trading outside-not disturbing the bots and how much cash I
       *       can safely withdraw with and without margin-loan."
       *
       *  Only rendered in Total mode (Sergey 2026-04-30: "We only need
       *  Spend/Withdraw without disturbing Bots only at Total Tab").
       *  Excl. bots mode is already a bot-safe view of the whole page,
       *  so the callouts would just restate the headline. In Total mode
       *  the page shows all-money figures and the callouts translate
       *  them into "but here's what's actually free of bot reservations". */}
      {mode === "total" && (
        <Box sx={{ mt: 2.5 }}>
          <PlainEnglishSummary current={exBotsSnapshot.current} />
        </Box>
      )}

      <Box sx={{ mt: 2.5 }}>
        <Section title="Buying Power">
          <BalanceRow
            label="RegT Buying Power"
            current={current.regTBuyingPower}
            term="regTBuyingPower"
          />
          <BalanceRow
            label="Day Trading Buying Power"
            current={current.dayTradingBuyingPower}
            term="dayTradingBuyingPower"
          />
          <BalanceRow
            label="Effective Buying Power"
            current={current.effectiveBuyingPower}
            term="effectiveBuyingPower"
          />
          <BalanceRow
            label="Non-Marginable Buying Power"
            current={current.nonMarginableBuyingPower}
            term="nonMarginableBuyingPower"
          />
        </Section>

        <Section title="Margin">
          <BalanceRow
            label="Initial Margin"
            current={current.initialMargin}
            term="initialMargin"
          />
          <BalanceRow
            label="Maintenance Margin"
            current={current.maintenanceMargin}
            term="maintenanceMargin"
          />
        </Section>

        <Section title="Cash">
          <BalanceRow label="Cash" current={current.cash} term="cash" />
          <BalanceRow
            label="Cash Withdrawable"
            current={current.cashWithdrawable}
            term="cashWithdrawable"
          />
          <BalanceRow
            label="Pending Transfer Out"
            current={current.pendingTransferOut}
            term="pendingTransferOut"
          />
          <BalanceRow
            label="Pending Transfer In"
            current={current.pendingTransferIn}
            term="pendingTransferIn"
          />
        </Section>

        <Section title="High Yield Cash">
          <BalanceRow
            label="Cash Accruing Interest"
            current={current.cashAccruingInterest}
            term="cashAccruingInterest"
          />
          <BalanceRow
            label="Monthly Accrued Interest"
            current={current.monthlyAccruedInterest}
            term="monthlyAccruedInterest"
          />
          <BalanceRow
            label="Total Accrued Interest"
            current={current.totalAccruedInterest}
            term="totalAccruedInterest"
          />
        </Section>

        <Section title="Positions">
          <BalanceRow
            label="Equity"
            current={current.equity}
            term="equity"
          />
          <BalanceRow
            label="Long Market Value"
            current={current.longMarketValue}
            term="longMarketValue"
          />
          <BalanceRow
            label="Short Market Value"
            current={current.shortMarketValue}
            term="shortMarketValue"
          />
          <BalanceRow
            label="Position Market Value"
            current={current.positionMarketValue}
            term="positionMarketValue"
          />
        </Section>

        <Section title="Miscellaneous">
          <BalanceRow
            label="Accrued Fees"
            current={current.accruedFees}
            term="accruedFees"
          />
          <BalanceRow
            label="Held ACH Deposits"
            current={current.heldAchDeposits}
            term="heldAchDeposits"
          />
          <BalanceRow
            label="Day Trade Count"
            current={current.dayTradeCount}
            format="count"
            term="dayTradeCount"
          />
        </Section>
      </Box>
    </Box>
  );
}

// ---- Plain-English summary ----------------------------------------------

/**
 * Two-callout summary block that turns the dense figure tables into
 * something a non-trader can scan in 5 seconds. Only rendered in
 * `total` mode — see the call site for the rationale. The values
 * passed in are always the *exBots* derivation so the callouts read
 * as a translation: "the Total numbers above include bot allocations,
 * here's what you actually have free of them".
 *
 *   • Spend now    — Effective Buying Power (exBots): "without
 *                    disturbing any bot's operation".
 *   • Withdraw now — Cash Withdrawable (exBots): the amount that can
 *                    leave the account today after leaving bot
 *                    allocations and the FINRA $2,000 minimum equity
 *                    buffer untouched.
 *
 * Layout: side-by-side on wide viewports, stacked on mobile. Each
 * callout is a soft-tinted card so it reads as guidance separate from
 * the dense rows beneath.
 */
function PlainEnglishSummary({ current }: { current: BalanceValues }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      sx={{ mt: 0 }}
    >
      <SummaryCallout
        title="Spend now without disturbing bots"
        body="How much you can deploy on manual trades right now without touching capital allocated to your bots."
        value={current.effectiveBuyingPower}
        tone="primary"
      />
      <SummaryCallout
        title="Withdraw safely (no bot impact)"
        body="Cash you can pull out today after leaving bot allocations and the FINRA $2,000 minimum equity buffer in place."
        value={current.cashWithdrawable}
        tone="success"
      />
    </Stack>
  );
}

function SummaryCallout({
  title,
  body,
  value,
  tone
}: {
  title: string;
  body: string;
  value: number;
  tone: "primary" | "success";
}) {
  return (
    <Box
      sx={(t: Theme) => ({
        flex: 1,
        minWidth: 0,
        borderRadius: 2,
        px: 1.75,
        py: 1.5,
        bgcolor: alpha(
          tone === "primary" ? t.palette.primary.main : t.palette.success.main,
          0.08
        ),
        border: `1px solid ${alpha(
          tone === "primary" ? t.palette.primary.main : t.palette.success.main,
          0.22
        )}`
      })}
    >
      <Typography
        variant="caption"
        sx={(t: Theme) => ({
          display: "block",
          fontWeight: 700,
          letterSpacing: 0.3,
          textTransform: "uppercase",
          color:
            tone === "primary" ? t.palette.primary.main : t.palette.success.main,
          mb: 0.5
        })}
      >
        {title}
      </Typography>
      <Typography
        variant="h5"
        sx={{
          fontWeight: 800,
          fontFeatureSettings: '"tnum" 1',
          lineHeight: 1.15,
          color: "text.primary"
        }}
      >
        {formatUSD(Math.max(0, value))}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: "text.secondary", mt: 0.5, lineHeight: 1.45 }}
      >
        {body}
      </Typography>
    </Box>
  );
}

// ---- Hero ----------------------------------------------------------------

function HeroSection({
  current,
  mode,
  onModeChange,
  isAggregate,
  accountCount
}: {
  current: BalanceValues;
  mode: BalanceMode;
  onModeChange: (m: BalanceMode) => void;
  isAggregate: boolean;
  accountCount: number;
}) {
  // Derived margin portion — what the broker is lending against your
  // long positions, after netting your cash. The hero headline above
  // already shows total Effective Buying Power, so we no longer
  // duplicate it as a "Total" row (Sergey 2026-04-28).
  const marginPortion = current.effectiveBuyingPower - current.cash;

  return (
    <Box>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1 }}
      >
        <Typography variant="overline" sx={{ color: "text.secondary", letterSpacing: 0.5 }}>
          Buying power
          {isAggregate && accountCount > 1 && (
            <Box component="span" sx={{ ml: 1, color: "text.secondary" }}>
              · {accountCount} accounts
            </Box>
          )}
        </Typography>
        <ModeToggle value={mode} onChange={onModeChange} />
      </Stack>

      <Typography
        variant="h3"
        sx={{
          fontWeight: 800,
          fontFeatureSettings: '"tnum" 1',
          lineHeight: 1.1,
          mt: 0.25
        }}
      >
        {formatUSD(current.effectiveBuyingPower)}
      </Typography>

      <Box sx={{ mt: 1.5, borderTop: (t) => `1px solid ${t.palette.divider}` }}>
        <HeroRow label="Cash" value={current.cash} term="cash" />
        <HeroRow
          label="Margin total"
          value={marginPortion > 0 ? marginPortion : 0}
          term="marginTotal"
        />
      </Box>
    </Box>
  );
}

function HeroRow({
  label,
  value,
  bold,
  term
}: {
  label: string;
  value: number;
  bold?: boolean;
  /** Optional TERMS key — when set, renders an (i) icon next to the
   *  label that opens a tooltip with the metric definition. */
  term?: TermKey;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      sx={{
        py: 1.5,
        borderBottom: (t) => `1px solid ${t.palette.divider}`
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          variant="body1"
          sx={{ fontWeight: bold ? 700 : 500, color: "text.primary" }}
        >
          {label}
        </Typography>
        {term && <InfoTip term={term} />}
      </Stack>
      <Typography
        variant="body1"
        sx={{
          fontWeight: bold ? 700 : 600,
          fontFeatureSettings: '"tnum" 1',
          whiteSpace: "nowrap"
        }}
      >
        {formatUSD(value)}
      </Typography>
    </Stack>
  );
}

function ModeToggle({
  value,
  onChange
}: {
  value: BalanceMode;
  onChange: (m: BalanceMode) => void;
}) {
  return (
    <ToggleButtonGroup
      exclusive
      value={value}
      size="small"
      onChange={(_e, v) => {
        if (v) onChange(v as BalanceMode);
      }}
      sx={{
        "& .MuiToggleButton-root": {
          textTransform: "none",
          fontWeight: 600,
          // Apr-2026 typography sweep — match PositionsFilterBar's
          // Side toggle and Bots/Orders Simple/Advanced toggles.
          fontSize: "1rem",
          height: 36,
          px: 1.5,
          py: 0.25
        }
      }}
    >
      <ToggleButton value="total">Total</ToggleButton>
      <ToggleButton value="exBots">Excl. bots</ToggleButton>
    </ToggleButtonGroup>
  );
}

// ---- Section + row ------------------------------------------------------

function Section({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant="body1"
        sx={{ fontWeight: 700, color: "text.primary", mb: 1 }}
      >
        {title}
      </Typography>
      <Divider />
      {children}
    </Box>
  );
}

/**
 * Single-column row — label on the left, current value on the right.
 *
 * The previous implementation showed a "Last Close" column alongside
 * "Current" (Alpaca-reference); Sergey 2026-04-27 dropped it because
 * the two columns showed near-identical values overnight and read as
 * a duplication. The `close` parameter is preserved on the function
 * signature so existing callers don't have to be touched, but the
 * value is no longer rendered.
 *
 * Typography sweep to body1 (1rem) matches the "Buying Power"
 * baseline anchoring the rest of the page (Bots/Orders/Positions
 * advanced views all use the same scale).
 *
 * `format="count"` switches the formatter to integer rendering — used
 * for Day Trade Count, which would read "$0.00" under default USD
 * formatting.
 */
function BalanceRow({
  label,
  current,
  format = "usd",
  term
}: {
  label: string;
  /** Kept for backwards compatibility — ignored as of 2026-04-27. */
  close?: number | null;
  current: number | null;
  format?: "usd" | "count";
  /** Optional TERMS key — renders an (i) icon next to the label
   *  with a tooltip that explains the metric on hover/tap. */
  term?: TermKey;
}) {
  const renderValue = (v: number | null) => {
    if (v === null) return EM_DASH;
    if (format === "count") return v.toString();
    return formatUSD(v);
  };

  return (
    <Box
      sx={(t: Theme) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        // Material Design list-item: bumped 8dp → 12dp top/bottom so
        // rows breathe at the body1 scale without feeling cramped.
        py: 1.5,
        borderBottom: `1px solid ${t.palette.divider}`,
        "&:last-of-type": { borderBottom: 0 }
      })}
    >
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", fontWeight: 500 }}
        >
          {label}
        </Typography>
        {term && <InfoTip term={term} />}
      </Stack>
      <Typography
        variant="body1"
        sx={{
          textAlign: "right",
          fontWeight: 700,
          color: "text.primary",
          fontFeatureSettings: '"tnum" 1',
          whiteSpace: "nowrap"
        }}
      >
        {renderValue(current)}
      </Typography>
    </Box>
  );
}

/**
 * Small information icon paired with a Tooltip that surfaces the
 * matching TERMS entry. Used next to balance-row labels so users can
 * hover (or tap on mobile) for a 1–2 sentence explanation of each
 * metric without cluttering the main rows.
 *
 * Typography sweep — MUI's default Tooltip caps text at ~10–11px which
 * is too small to comfortably read on a phone screen (Sergey
 * 2026-04-28). The slotProps override below pushes the tooltip body
 * to body2 (0.875rem) and the title to body1 (1rem), and widens the
 * tooltip to 320px so the explanations don't wrap into 8 lines.
 */
function InfoTip({ term }: { term: TermKey }) {
  const t = TERMS[term];
  return (
    <Tooltip
      arrow
      enterTouchDelay={0}
      leaveTouchDelay={8000}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 320,
            fontSize: "0.875rem",
            lineHeight: 1.5,
            px: 1.5,
            py: 1.25,
            bgcolor: (t) => alpha(t.palette.background.paper, 0.97),
            color: "text.primary",
            border: (t) => `1px solid ${t.palette.divider}`,
            boxShadow: (t) =>
              `0 4px 16px ${alpha(t.palette.common.black, 0.32)}`
          }
        },
        arrow: {
          sx: {
            color: (t) => alpha(t.palette.background.paper, 0.97)
          }
        }
      }}
      title={
        <Box>
          <Typography
            variant="body1"
            sx={{
              display: "block",
              fontWeight: 700,
              mb: 0.5,
              color: "text.primary"
            }}
          >
            {t.title}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              display: "block",
              lineHeight: 1.5,
              color: "text.secondary"
            }}
          >
            {t.body}
          </Typography>
        </Box>
      }
      placement="top"
    >
      <InfoOutlined
        sx={{
          fontSize: 18,
          color: "text.secondary",
          cursor: "help",
          opacity: 0.85,
          "&:hover": { opacity: 1 }
        }}
      />
    </Tooltip>
  );
}

const EM_DASH = "—";
